import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  logout,
  getScreenCode,
  getMasjidId,
  getScreenValue,
} from "../../services/authService";
import { screenDB } from "../../db/screenDb";
import { timetableDB } from "../../db/indexedDb";
import { azureStorageService } from "../../services/azureStorageService";
import { useConnectionQuality } from "../../hooks/useConnectionQuality"; // ✅ Add import for the new hook
import { getCurrentScreenIdentifier } from "../../utils/screenStorage";
import "./AuthMenu.css";

const AuthMenu: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isFullYearDownloading, setIsFullYearDownloading] = useState(false);
  const navigate = useNavigate();
  const connectionQuality = useConnectionQuality(); // ✅ Use the hook instead of basic navigator.onLine

  // Get screenCode and masjidId from namespaced storage
  const screenCode = getScreenCode() || "";
  const masjidId = getMasjidId() || "";
  const masjidName = getScreenValue("masjidName") || "";

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  // Force refresh: Update to use namespaced screenCode
  const handleForceRefresh = async () => {
    if (
      !confirm(
        "This will clear cached content for this screen and reload. Continue?",
      )
    ) {
      return;
    }

    try {
      const currentScreenCode = getScreenCode();
      const screenIdentifier = getCurrentScreenIdentifier();
      if (!currentScreenCode || !screenIdentifier) {
        console.warn("No screen code found, skipping cache cleanup");
        return;
      }

      console.log(
        `Force refresh: Starting cache cleanup for screen ${currentScreenCode} (${screenIdentifier})...`,
      );

      // Clear screen settings from IndexedDB for current screen only
      await screenDB.saveScreenData(currentScreenCode, undefined as any); // Clear screen data

      // Clear only screen-specific Cache Storage entries
      if ("caches" in window) {
        try {
          const cacheKeys = await caches.keys();
          const screenCacheKeys = cacheKeys.filter(
            (key) =>
              key.includes(currentScreenCode) || // Screen-specific cache
              key.includes(screenIdentifier) || // Namespaced screen identifier
              key.includes("screen-assets") || // General screen assets cache
              key.includes("timetables"), // Timetable cache
          );

          await Promise.all(screenCacheKeys.map((key) => caches.delete(key)));
          console.log(
            `Force refresh: cleared cache storage for screen ${currentScreenCode}`,
          );
        } catch (e) {
          console.warn("Cache Storage clear failed:", e);
        }
      }

      // Unregister service workers to ensure fresh assets
      if ("serviceWorker" in navigator) {
        try {
          const registrations =
            await navigator.serviceWorker.getRegistrations();
          await Promise.all(registrations.map((reg) => reg.unregister()));
          console.log("Force refresh: unregistered service workers");
        } catch (e) {
          console.warn("Service worker unregister failed:", e);
        }
      }
    } catch (err) {
      console.error("Error during presentation cache cleanup:", err);
    } finally {
      console.log("Force refresh: cleanup complete, reloading page...");
      window.location.reload();
    }
  };

  // Helper function to download 12 months for a given target year
  async function downloadFullTimetableForYear(targetYear: string) {
    console.log(`Starting download for full year ${targetYear}...`);
    for (let m = 1; m <= 12; m++) {
      const month = m.toString(); // No leading zero padding
      console.log(`Downloading timetable for ${targetYear}-${month}...`);
      try {
        await downloadMonth(masjidId.toString(), targetYear, month);
        console.log(`✅ Successfully downloaded ${targetYear}-${month}`);
      } catch (error) {
        // ✅ Continue to next month even if this one fails
        const errorMsg =
          typeof error === "object" && error !== null && "message" in error
            ? (error as { message?: string }).message
            : String(error);
        console.warn(`❌ Skipping ${targetYear}-${month}: ${errorMsg}`);
      }
    }
    console.log(`Completed download for full year ${targetYear}`);
  }

  // New: Trigger a one-time download of the full 12‐month timetable for the current year.
  const downloadFullCurrentYear = async () => {
    console.log("Starting full current year download...");
    console.log("MasjidId:", masjidId);

    if (!masjidId) {
      alert("No masjid ID found. Please re-authenticate.");
      return;
    }

    setIsFullYearDownloading(true);
    try {
      const now = new Date();
      const currentYear = now.getFullYear().toString();
      console.log("Downloading full year for:", currentYear);
      await downloadFullTimetableForYear(currentYear);
      alert("Full current year timetable download completed!");
    } catch (err) {
      console.error("Full current year download error:", err);
      alert("There was an error downloading the full current year timetable.");
    } finally {
      setIsFullYearDownloading(false);
    }
  };

  // New: Trigger a one-time download of the full 12‐month timetable for the next year.
  const downloadFullNextYear = async () => {
    console.log("Starting full next year download...");
    console.log("MasjidId:", masjidId);

    if (!masjidId) {
      alert("No masjid ID found. Please re-authenticate.");
      return;
    }

    setIsFullYearDownloading(true);
    try {
      const now = new Date();
      const nextYear = (now.getFullYear() + 1).toString();
      console.log("Downloading full year for:", nextYear);
      await downloadFullTimetableForYear(nextYear);
      alert("Full next year timetable download completed!");
    } catch (err) {
      console.error("Full next year download error:", err);
      alert("There was an error downloading the full next year timetable.");
    } finally {
      setIsFullYearDownloading(false);
    }
  };

  // Helper function to download one month – reuse your existing logic.
  async function downloadMonth(masjidId: string, year: string, month: string) {
    const blobPath = `timetables/${masjidId}/${year}/${month}.json.gz`;
    console.log(`📥 Starting download for ${blobPath}`);
    try {
      const { data: latest, eTag: latestETag } =
        await azureStorageService.getPrayerTimesFromBlob(masjidId, blobPath);

      // ✅ Only save if data exists
      if (latest) {
        await timetableDB.saveTimetable(year, month, masjidId, {
          ...latest,
          eTag: latestETag,
        });
        console.log(`💾 Saved timetable for ${year}-${month}`);
      } else {
        console.warn(`⚠️ No data received for ${year}-${month}, skipping save`);
      }
    } catch (error) {
      console.error(
        `💥 Error downloading timetable for ${year}-${month}:`,
        error,
      );
      // ✅ Don't throw - allow calling function to continue
      // This handles cases where the month file doesn't exist
    }
  }

  // Optionally, show "Download Next Year" if current month is May or onwards
  const canDownloadNextYear = () => {
    const month = new Date().getMonth() + 1; // 1 = January ... 12 = December
    return month >= 5; // Allow next year download if May or later
  };

  // Helper to check if actions should be disabled
  const isNetworkRequiredActionDisabled = () => {
    return connectionQuality === "offline" || connectionQuality === "slow";
  };

  return (
    <div className="auth-menu-container">
      <button
        className="auth-menu-button"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Settings"
      >
        <div
          className={`connection-status ${
            connectionQuality === "online"
              ? "online"
              : connectionQuality === "slow"
                ? "slow"
                : "offline"
          }`} // ✅ Update for three states
          aria-label={
            connectionQuality === "online"
              ? "Online"
              : connectionQuality === "slow"
                ? "Slow Connection"
                : "Offline"
          }
          role="status"
        ></div>
        <i className="bi bi-gear"></i>
      </button>
      {isOpen && (
        <div className="auth-menu-dropdown">
          <div className="auth-menu-header">
            <h4>Display Settings</h4>
            <button
              className="auth-menu-close"
              onClick={() => setIsOpen(false)}
              aria-label="Close menu"
            >
              <i className="bi bi-x"></i>
            </button>
          </div>
          <div className="auth-menu-content">
            <div className="auth-menu-info">
              <div>
                <strong>Screen Code:</strong> {screenCode}
              </div>
              <div>
                <strong>Masjid:</strong> {masjidName} (ID: {masjidId})
              </div>

              <div
                className={`connection-status-text ${
                  connectionQuality === "online"
                    ? "online"
                    : connectionQuality === "slow"
                      ? "slow"
                      : "offline"
                }`}
              >
                <strong>Connection:</strong>{" "}
                {connectionQuality === "online"
                  ? "Online"
                  : connectionQuality === "slow"
                    ? "Slow (limited features)"
                    : "Offline (limited features)"}
              </div>
            </div>
            <div className="auth-menu-actions">
              <button
                className="auth-menu-action-button refresh"
                onClick={handleForceRefresh}
                disabled={isNetworkRequiredActionDisabled()} // ✅ Disable when offline/slow to prevent cache/service worker disruption
                title={
                  isNetworkRequiredActionDisabled()
                    ? "Requires online connection"
                    : ""
                }
              >
                <i className="bi bi-arrow-clockwise"></i> Force Refresh
              </button>
              <button
                className="auth-menu-action-button logout"
                onClick={handleLogout}
                disabled={isNetworkRequiredActionDisabled()} // ✅ Disable to prevent sign-off issues
                title={
                  isNetworkRequiredActionDisabled()
                    ? "Requires online connection"
                    : ""
                }
              >
                <i className="bi bi-box-arrow-right"></i> Sign Out
              </button>
              <button
                className="auth-menu-action-button full-year"
                onClick={downloadFullCurrentYear}
                disabled={
                  isFullYearDownloading || isNetworkRequiredActionDisabled() // ✅ Disable downloads offline/slow
                }
                title={
                  isNetworkRequiredActionDisabled()
                    ? "Requires online connection"
                    : ""
                }
              >
                <i className="bi bi-download"></i>{" "}
                {isFullYearDownloading
                  ? "Downloading..."
                  : "Download Full Current Year"}
              </button>
              {canDownloadNextYear() && (
                <button
                  className="auth-menu-action-button full-year"
                  onClick={downloadFullNextYear}
                  disabled={
                    isFullYearDownloading || isNetworkRequiredActionDisabled() // ✅ Disable downloads offline/slow
                  }
                  title={
                    isNetworkRequiredActionDisabled()
                      ? "Requires online connection"
                      : ""
                  }
                >
                  <i className="bi bi-download"></i>{" "}
                  {isFullYearDownloading
                    ? "Downloading..."
                    : "Download Full Next Year"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuthMenu;
